// CLI v0.8 — link service.
//
// The link artifact at `<project>/.cloudgrid/link.json` is the single
// source of truth for which server-side entity a directory is bound to.
// It is gitignored: the binding is a property of the local checkout, not
// of the code. See `docs/strategy/cli-ux-spec-v17.md` §4 for the spec
// and `docs/decisions/012-plug-verb-and-charged-state.md` for the
// architectural rationale.
//
// Helpers are exception-shaped (throw) rather than tuple-returning so
// they're ergonomic on the hot path of every entity-targeted verb.

import { existsSync, mkdirSync, readFileSync, renameSync, writeFileSync } from 'fs';
import { randomBytes } from 'crypto';
import { dirname, join } from 'path';
import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError } from '../utils.js';

/**
 * The shape persisted at `.cloudgrid/link.json`.
 *
 * `kind` is included per decision 012 — entities have one of two top-level
 * shapes (app or agent), and the CLI's per-verb branching (e.g. `info`
 * status copy, future `scaffold` defaults) needs it without an extra HTTP
 * round-trip. `realm` is omitted: every link this CLI ever writes is a
 * runtime; if/when an inspiration `init` path lands the field can be
 * added then.
 *
 * `entity_slug` is OPTIONAL on read for backwards compatibility — existing
 * link.json files written before this field landed continue to work via
 * the dev-runner fallback chain (link.entity_slug → yaml `name:` →
 * `basename(cwd)`). New writes (`init`, `plug --existing`) always include
 * it. Per decision 015 §"Identity-aware behavior".
 */
export interface LinkArtifact {
  /** Server-issued UUID — primary key, globally unique. */
  entity_id: string;
  /** Server-issued org UUID. */
  org_id: string;
  /** Human-readable org slug; display only, never used for resolution. */
  org_slug: string;
  /** Entity kind — `app` or `agent`. Pinned at init time, never changes. */
  kind: 'app' | 'agent';
  /**
   * Server-disambiguated entity slug — the canonical name returned by the
   * server (may differ from the user's requested slug if there was a
   * collision). Optional on disk: pre-existing links written before this
   * field landed will not have it; consumers fall back to yaml `name:`
   * then `basename(cwd)`.
   */
  entity_slug?: string;
  /** ISO-8601 timestamp — when the link was written by `init`. */
  created_at: string;
  /** CLI version that wrote the link — useful for future migrations. */
  cli_version: string;
}

/** Required fields validated when reading from disk. */
const REQUIRED_FIELDS: ReadonlyArray<keyof LinkArtifact> = [
  'entity_id',
  'org_id',
  'org_slug',
  'kind',
  'created_at',
  'cli_version',
];

/**
 * Required fields *other than* `kind`. Used by `readLinkOrRecover` so a
 * link.json from CLI ≤ 0.8.6 (which lacks `kind`) can be parsed and
 * recovered via the v2 entity API instead of throwing.
 */
const REQUIRED_FIELDS_EXCEPT_KIND: ReadonlyArray<Exclude<keyof LinkArtifact, 'kind'>> = [
  'entity_id',
  'org_id',
  'org_slug',
  'created_at',
  'cli_version',
];

const LINK_RELATIVE_PATH = join('.cloudgrid', 'link.json');
const GITIGNORE_FILENAME = '.gitignore';

/** Friendly message when no link is present. Wording per spec v17 §4. */
const NO_LINK_MESSAGE =
  "This directory isn't linked to a CloudGrid node. Run: cloudgrid init <kind> <name>";

/** Thrown when `.cloudgrid/link.json` exists but is invalid. */
export class MalformedLinkError extends CloudGridError {
  constructor(reason: string) {
    super(`Malformed .cloudgrid/link.json: ${reason}`);
    this.name = 'MalformedLinkError';
  }
}

/**
 * Thrown when the link's org disagrees with the active org.
 * The mismatch is the user's misconfiguration — CLI never auto-resolves.
 *
 * Voice intent matches spec v17 §5; the literal `cloudgrid switch` line
 * from §5 is dropped because that command does not exist in v0.8. Two valid
 * resolutions are offered (backlog 320): `--org <slug>` for a one-shot
 * override (spec §15 CI/CD precedence ladder), or `cloudgrid use <slug>` to
 * change the persisted active org. Both take the link's *canonical* slug.
 */
export class LinkOrgMismatchError extends CloudGridError {
  readonly linkOrgSlug: string;
  readonly activeOrgSlug: string;

  constructor(linkOrgSlug: string, activeOrgSlug: string) {
    super(
      `This directory is linked to entity in grid '${linkOrgSlug}'.\n` +
        `Your active grid is '${activeOrgSlug}'.\n` +
        `\n` +
        `To use this entity's grid for this command:  re-run with --org ${linkOrgSlug}\n` +
        `To switch your active grid permanently:       cloudgrid use ${linkOrgSlug}\n` +
        `To start a new entity here:                  rm -r .cloudgrid && cloudgrid init <kind> <name>`,
    );
    this.name = 'LinkOrgMismatchError';
    this.linkOrgSlug = linkOrgSlug;
    this.activeOrgSlug = activeOrgSlug;
  }
}

function linkPath(cwd: string): string {
  return join(cwd, LINK_RELATIVE_PATH);
}

/**
 * Read `<cwd>/.cloudgrid/link.json`.
 * - Returns `null` if the file is absent.
 * - Throws `MalformedLinkError` if the file is present but unreadable as a
 *   `LinkArtifact` (invalid JSON, wrong shape, missing required field).
 */
export function readLink(cwd: string = process.cwd()): LinkArtifact | null {
  const path = linkPath(cwd);
  if (!existsSync(path)) return null;

  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch (err) {
    throw new MalformedLinkError(
      `unreadable (${(err as Error).message})`,
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new MalformedLinkError(
      `invalid JSON (${(err as Error).message})`,
    );
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new MalformedLinkError('not a JSON object');
  }

  const obj = parsed as Record<string, unknown>;
  for (const field of REQUIRED_FIELDS) {
    if (typeof obj[field] !== 'string' || obj[field] === '') {
      throw new MalformedLinkError(`missing field '${field}'`);
    }
  }
  if (obj.kind !== 'app' && obj.kind !== 'agent') {
    throw new MalformedLinkError(`invalid kind '${String(obj.kind)}' (expected 'app' or 'agent')`);
  }

  // entity_slug is optional (backwards compat) but must be a non-empty
  // string when present — a malformed value is a user-visible signal that
  // something wrote a corrupt link.
  let entitySlug: string | undefined;
  if ('entity_slug' in obj) {
    if (typeof obj.entity_slug !== 'string' || obj.entity_slug === '') {
      throw new MalformedLinkError(`invalid entity_slug (expected non-empty string)`);
    }
    entitySlug = obj.entity_slug;
  }

  return {
    entity_id: obj.entity_id as string,
    org_id: obj.org_id as string,
    org_slug: obj.org_slug as string,
    kind: obj.kind,
    ...(entitySlug ? { entity_slug: entitySlug } : {}),
    created_at: obj.created_at as string,
    cli_version: obj.cli_version as string,
  };
}

/**
 * Write a `LinkArtifact` to `<cwd>/.cloudgrid/link.json`.
 * Creates the directory if absent. 2-space indent. Idempotent.
 *
 * The write is atomic: the JSON is written to a uniquely-named temp file in
 * the same directory, then `rename`d over the target. A same-directory
 * rename is atomic on POSIX, so a crash mid-write can never leave a
 * half-written `link.json` — the file is the only durable binding between a
 * checkout and its server-side entity, and a corrupted one bricks every
 * entity-targeted verb. The temp file shares the target's directory so the
 * rename stays on one filesystem (cross-device renames are not atomic).
 */
export function writeLink(link: LinkArtifact, cwd: string = process.cwd()): void {
  const path = linkPath(cwd);
  mkdirSync(dirname(path), { recursive: true });
  const tmpPath = `${path}.${randomBytes(6).toString('hex')}.tmp`;
  writeFileSync(tmpPath, JSON.stringify(link, null, 2), 'utf-8');
  renameSync(tmpPath, path);
}

/**
 * Like `readLink` but throws a friendly `CloudGridError` when no link
 * exists. Use this from entity-targeted verbs (every verb except `init`).
 *
 * If the link exists but is malformed, the underlying `MalformedLinkError`
 * propagates — that's a real failure mode we want surfaced, not papered
 * over with the friendly "no link" message.
 */
export function requireLink(cwd: string = process.cwd()): LinkArtifact {
  const link = readLink(cwd);
  if (link === null) {
    throw new CloudGridError(NO_LINK_MESSAGE);
  }
  return link;
}

/**
 * Validate that the link's org matches the currently active org.
 * Throws `LinkOrgMismatchError` (spec v17 §5) if they disagree. The CLI
 * never auto-switches; the user resolves the mismatch explicitly.
 *
 * Comparison is by `org_id` — stable across slug migrations (backlog 308).
 * `link.org_slug` is display-only and may carry a stale legacy value (e.g.
 * `_system_` written pre-migration-003); telling the user to re-run with
 * `--org _system_` would not work. When `orgs` is supplied, the error
 * message displays the *current* canonical slug resolved from the link's
 * `org_id`, falling back to the stored `org_slug` only when no list is
 * available.
 */
export function validateLinkVsActiveOrg(
  link: LinkArtifact,
  activeOrg: { org_id: string; slug: string },
  orgs?: ReadonlyArray<{ org_id: string; slug: string }>,
): void {
  if (link.org_id !== activeOrg.org_id) {
    const displaySlug =
      orgs?.find((o) => o.org_id === link.org_id)?.slug ?? link.org_slug;
    throw new LinkOrgMismatchError(displaySlug, activeOrg.slug);
  }
}

/**
 * Append a single line to `<cwd>/.gitignore`, creating the file if absent.
 *
 * Idempotent: if the line is already present anywhere in the file
 * (compared after `.trim()`), the file is left untouched. When appending
 * to a file that lacks a trailing newline, a leading newline is inserted
 * so the new entry sits on its own line.
 */
export function appendGitignoreLine(
  line: string,
  cwd: string = process.cwd(),
): void {
  const path = join(cwd, GITIGNORE_FILENAME);
  const target = line.trim();

  if (!existsSync(path)) {
    writeFileSync(path, `${line}\n`, 'utf-8');
    return;
  }

  const existing = readFileSync(path, 'utf-8');
  const present = existing
    .split('\n')
    .some((existingLine) => existingLine.trim() === target);
  if (present) return;

  const prefix = existing.endsWith('\n') ? '' : '\n';
  writeFileSync(path, `${existing}${prefix}${line}\n`, 'utf-8');
}

// ── Backlog 090: kind auto-recovery ────────────────────────────────────────
//
// `link.json` files written by CLI ≤ 0.8.6 lack the `kind` field. The
// strict parser (`readLink`) treats that as a malformed file. Every entity
// plugged before 0.8.7 is in this state — fleet-wide regression that
// blocks redeploy until the developer manually edits the file.
//
// Recovery flow: parse the file, verify every required field *other than*
// `kind`, then ask the server (`GET /api/v2/runtimes/:id`) what `kind` the
// entity has. On success, write the file back with `kind` filled in so
// subsequent reads are no-ops. On 404 / network / 5xx, fall back to `'app'`
// (per non-negotiable rule "apps and agents are the same entity type" —
// `agent` is a metadata flip), surface a stderr warning, and still write
// back so we don't keep retrying on every command.

/**
 * Parse a link.json from disk, allowing `kind` to be missing or invalid.
 * Returns the partial link plus a flag indicating whether `kind` recovery
 * is needed. Other required fields (`entity_id`, `org_id`, etc.) are still
 * enforced — a missing `entity_id` is unrecoverable, so we throw the same
 * `MalformedLinkError` the strict parser would have.
 */
function readLinkAllowMissingKind(
  cwd: string,
):
  | { kindNeeded: false; link: LinkArtifact }
  | { kindNeeded: true; partial: Omit<LinkArtifact, 'kind'> }
  | null {
  const path = linkPath(cwd);
  if (!existsSync(path)) return null;

  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch (err) {
    throw new MalformedLinkError(`unreadable (${(err as Error).message})`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new MalformedLinkError(`invalid JSON (${(err as Error).message})`);
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new MalformedLinkError('not a JSON object');
  }

  const obj = parsed as Record<string, unknown>;
  for (const field of REQUIRED_FIELDS_EXCEPT_KIND) {
    if (typeof obj[field] !== 'string' || obj[field] === '') {
      throw new MalformedLinkError(`missing field '${field}'`);
    }
  }

  let entitySlug: string | undefined;
  if ('entity_slug' in obj) {
    if (typeof obj.entity_slug !== 'string' || obj.entity_slug === '') {
      throw new MalformedLinkError(`invalid entity_slug (expected non-empty string)`);
    }
    entitySlug = obj.entity_slug;
  }

  const base: Omit<LinkArtifact, 'kind'> = {
    entity_id: obj.entity_id as string,
    org_id: obj.org_id as string,
    org_slug: obj.org_slug as string,
    ...(entitySlug ? { entity_slug: entitySlug } : {}),
    created_at: obj.created_at as string,
    cli_version: obj.cli_version as string,
  };

  if (obj.kind === 'app' || obj.kind === 'agent') {
    return { kindNeeded: false, link: { ...base, kind: obj.kind } };
  }
  // Missing or unrecognised kind — recovery candidate.
  return { kindNeeded: true, partial: base };
}

/** Server response shape for `GET /api/v2/runtimes/:id`, narrowed to the
 *  field this module needs. The endpoint returns `kind: 'app' | 'agent'`
 *  derived from the entity's `agent:` block. */
interface RuntimeKindResponse {
  kind?: unknown;
}

// ── Backlog 309: legacy org_slug auto-repair ───────────────────────────────
//
// Migration 003 (2026-05-03) renamed the canonical org slug `_system_` →
// `system` on the server but did not touch per-checkout `link.json` files.
// `org_slug` is display-only — resolution is by the stable `org_id` (backlog
// 308) — but a stale value still surfaces in `info`/`unplug` output and in
// mismatch errors. Rather than make developers run `cloudgrid link repair`
// by hand, the on-read recovery path (`readLinkOrRecover`) transparently
// re-resolves the canonical slug from `org_id` and rewrites the file.

/**
 * Org slugs known to be stale post-migration-003. When a `link.json` carries
 * one of these, `readLinkOrRecover` re-resolves the canonical slug from the
 * link's `org_id` and rewrites the file. Kept as an explicit allowlist so a
 * developer who genuinely renamed an org doesn't get their slug "repaired"
 * out from under them — only known-legacy values trigger the rewrite.
 */
const LEGACY_ORG_SLUGS: ReadonlySet<string> = new Set(['_system_']);

/**
 * Resolve the entity's `kind` via `GET /api/v2/runtimes/:id`. On any API
 * failure, falls back to `'app'` (per "apps and agents are the same entity
 * type" — `agent` is a metadata flip) and surfaces a one-line stderr notice
 * so the developer knows what happened.
 */
async function recoverKind(
  client: IV2Client,
  partial: Omit<LinkArtifact, 'kind'>,
): Promise<'app' | 'agent'> {
  try {
    const res = await client.get<RuntimeKindResponse>(
      `/api/v2/runtimes/${partial.entity_id}`,
    );
    if (res?.kind === 'agent') return 'agent';
    if (res?.kind === 'app') return 'app';
    // Server omitted kind — apply the same "agent is a metadata flip" default.
    process.stderr.write(
      `Notice: link.json was missing 'kind'. Server response did not specify kind; defaulting to 'app'.\n`,
    );
    return 'app';
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) {
      process.stderr.write(
        `Notice: link.json was missing 'kind' and the entity was not found on the server (404). Defaulting to 'app'. ` +
          `If this is an agent, edit .cloudgrid/link.json and set "kind": "agent".\n`,
      );
    } else {
      const detail = err instanceof Error ? err.message : String(err);
      process.stderr.write(
        `Notice: link.json was missing 'kind' and the server lookup failed (${detail}). Defaulting to 'app'.\n`,
      );
    }
    return 'app';
  }
}

/**
 * Backlog 309: if the link's `org_slug` is a known legacy value, re-resolve
 * the current canonical slug from the stable `org_id` and return a repaired
 * link. Returns `null` when no repair is needed or possible — auto-repair is
 * best-effort and must never break the read path:
 *
 *   - slug is not a known-legacy value → no repair
 *   - `getOrgs` fails (network / auth) → leave the stale slug in place
 *   - `org_id` doesn't resolve to an accessible org → leave it in place
 *   - resolved slug already matches → no-op (idempotent)
 *
 * On a successful rewrite, emits a single §23-voice stderr notice.
 */
async function repairLegacyOrgSlug(
  client: IV2Client,
  link: LinkArtifact,
): Promise<LinkArtifact | null> {
  if (!LEGACY_ORG_SLUGS.has(link.org_slug)) return null;

  let orgs: ReadonlyArray<{ org_id: string; slug: string }>;
  try {
    orgs = await client.getOrgs();
  } catch {
    return null;
  }

  const match = orgs.find((o) => o.org_id === link.org_id);
  if (!match || match.slug === link.org_slug) return null;

  process.stderr.write(
    `Notice: repaired stale org_slug in .cloudgrid/link.json: ` +
      `'${link.org_slug}' → '${match.slug}' (org_id ${link.org_id} unchanged).\n`,
  );
  return { ...link, org_slug: match.slug };
}

/** Options for {@link readLinkOrRecover}. */
export interface ReadLinkOrRecoverOpts {
  /**
   * Whether to transparently rewrite a known-legacy `org_slug` to its
   * current canonical value (backlog 309). Defaults to `true`. The explicit
   * `cloudgrid link repair` verb passes `false` so it can read the *stale*
   * value and report (or `--dry-run`) the change itself instead of having
   * the read silently apply it first.
   */
  autoRepairOrgSlug?: boolean;
}

/**
 * Read `<cwd>/.cloudgrid/link.json`, recovering a missing `kind` field via
 * the v2 entity API and rewriting a stale legacy `org_slug` (backlog 309),
 * then writing any corrected link back to disk.
 *
 * Returns `null` if the file is absent, mirroring `readLink`. Throws
 * `MalformedLinkError` only for unrecoverable malformations (invalid JSON,
 * missing non-`kind` required fields). A missing or invalid `kind` field
 * triggers an API round-trip; on any error from the API the recovery
 * silently falls back to `'app'` and surfaces a one-line stderr warning so
 * the developer knows what happened. A known-legacy `org_slug` (e.g.
 * pre-migration-003 `_system_`) is re-resolved from the stable `org_id` and
 * rewritten; that repair is best-effort and never blocks the read.
 *
 * Both recoveries are idempotent: once written back, subsequent reads need
 * no API call and no write.
 */
export async function readLinkOrRecover(
  client: IV2Client,
  cwd: string = process.cwd(),
  opts: ReadLinkOrRecoverOpts = {},
): Promise<LinkArtifact | null> {
  const parsed = readLinkAllowMissingKind(cwd);
  if (parsed === null) return null;

  let link: LinkArtifact;
  let needsWrite = false;

  if (parsed.kindNeeded) {
    link = { ...parsed.partial, kind: await recoverKind(client, parsed.partial) };
    needsWrite = true;
  } else {
    link = parsed.link;
  }

  if (opts.autoRepairOrgSlug !== false) {
    const repaired = await repairLegacyOrgSlug(client, link);
    if (repaired !== null) {
      link = repaired;
      needsWrite = true;
    }
  }

  if (needsWrite) writeLink(link, cwd);
  return link;
}

/**
 * Like `requireLink` but uses `readLinkOrRecover` so a kind-less link from
 * CLI ≤ 0.8.6 doesn't error the user out of every entity-targeted verb.
 */
export async function requireLinkOrRecover(
  client: IV2Client,
  cwd: string = process.cwd(),
): Promise<LinkArtifact> {
  const link = await readLinkOrRecover(client, cwd);
  if (link === null) {
    throw new CloudGridError(NO_LINK_MESSAGE);
  }
  return link;
}
